import React, { useEffect, useState, useCallback, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Heart,
  Sparkles,
  Plus,
} from "lucide-react";
import { Button } from "./ui/button";
import { services } from "../mock";

const Services = () => {
  const autoplay = useRef(
    Autoplay({
      delay: 6000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      skipSnaps: false,
    },
    [autoplay.current]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    autoplay.current?.reset();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    autoplay.current?.reset();
  }, [emblaApi]);
  const scrollTo = useCallback(
    (i) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(i);
      autoplay.current?.reset();
    },
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => emblaApi?.off("select", onSelect);
  }, [emblaApi]);

  // Keyboard nav for the carousel section
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") scrollPrev();
      else if (e.key === "ArrowRight") scrollNext();
    };
    const el = document.getElementById("services");
    el?.addEventListener("keydown", onKey);
    return () => el?.removeEventListener("keydown", onKey);
  }, [scrollPrev, scrollNext]);

  return (
    <section
      id="services"
      tabIndex={-1}
      className="relative py-20 lg:py-28 bg-[#faf3eb] outline-none"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Intro */}
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#daae9c]">
            <span className="w-6 h-px bg-sterling-dark" />
            Our Service Packages
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink mt-4 leading-[1.05] text-balance">
            Six tailored packages,{" "}
            <span className="italic text-[#daae9c]">one refined standard.</span>
          </h2>
          <p className="mt-5 text-ink/70 leading-relaxed">
            Each package is structured to match the moment your home is in —
            from quick maintenance resets to inspection-ready bond cleans.
            Choose a package, or let us tailor one to you.
          </p>
        </div>

        {/* Tab pills row */}
        <div className="mt-12 flex flex-wrap gap-2">
          {services.map((s, i) => (
            <button
              key={s.id}
              onClick={() => scrollTo(i)}
              data-testid={`pkg-tab-${s.id}`}
              className={`px-5 h-11 rounded-full text-sm transition-all border ${
                selectedIndex === i
                  ? "bg-ink text-cream border-ink"
                  : "bg-white text-ink border-[#ead8c9] hover:border-sterling-dark"
              }`}
            >
              {s.short}
            </button>
          ))}
        </div>

        {/* Embla viewport with floating arrows */}
        <div className="relative mt-10">
          <div
            className="overflow-hidden"
            ref={emblaRef}
            data-testid="pkg-carousel"
          >
            <div className="flex">
              {services.map((s) => (
                <div
                  key={s.id}
                  className="flex-[0_0_100%] min-w-0 px-1"
                  data-testid={`pkg-slide-${s.id}`}
                >
                  <PackageDetail service={s} />
                </div>
              ))}
            </div>
          </div>

          {/* Overlay arrows */}
          <button
            onClick={scrollPrev}
            aria-label="Previous package"
            data-testid="pkg-prev"
            className="hidden md:flex absolute left-0 lg:-left-6 top-1/3 -translate-y-1/2 h-12 w-12 rounded-full bg-white/95 backdrop-blur shadow-[0_8px_24px_-8px_rgba(138,93,72,0.35)] border border-[#ead8c9] hover:border-sterling-dark hover:bg-[#daae9c] hover:text-white text-ink transition-colors items-center justify-center z-10"
          >
            <ArrowLeft size={20} />
          </button>
          <button
            onClick={scrollNext}
            aria-label="Next package"
            data-testid="pkg-next"
            className="hidden md:flex absolute right-0 lg:-right-6 top-1/3 -translate-y-1/2 h-12 w-12 rounded-full bg-white/95 backdrop-blur shadow-[0_8px_24px_-8px_rgba(138,93,72,0.35)] border border-[#ead8c9] hover:border-sterling-dark hover:bg-[#daae9c] hover:text-white text-ink transition-colors items-center justify-center z-10"
          >
            <ArrowRight size={20} />
          </button>

          {/* Mobile arrow row (carousel buttons too small to overlay on mobile) */}
          <div className="md:hidden mt-4 flex items-center justify-center gap-3">
            <button
              onClick={scrollPrev}
              aria-label="Previous package"
              data-testid="pkg-prev-mobile"
              className="h-11 w-11 rounded-full bg-white border border-[#ead8c9] hover:border-sterling-dark text-ink flex items-center justify-center"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              onClick={scrollNext}
              aria-label="Next package"
              data-testid="pkg-next-mobile"
              className="h-11 w-11 rounded-full bg-white border border-[#ead8c9] hover:border-sterling-dark text-ink flex items-center justify-center"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        {/* Pagination dots */}
        <div
          className="mt-8 flex items-center justify-center gap-2"
          data-testid="pkg-dots"
        >
          {services.map((s, i) => (
            <button
              key={s.id}
              onClick={() => scrollTo(i)}
              aria-label={`Go to ${s.short}`}
              data-testid={`pkg-dot-${s.id}`}
              className={`transition-all rounded-full ${
                selectedIndex === i
                  ? "w-8 h-2.5 bg-[#daae9c]"
                  : "w-2.5 h-2.5 bg-[#ead8c9] hover:bg-sterling-dark/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// ----- Package detail view (used inside each carousel slide) -----
const PackageDetail = ({ service }) => (
  <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
    {/* Image + price card */}
    <div className="lg:col-span-5">
      <div className="relative overflow-hidden rounded-2xl group bg-white border border-[#ead8c9]">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-auto max-h-[640px] object-contain transition-transform duration-700 group-hover:scale-[1.02]"
        />
        {service.badge && (
          <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[#8a5d48] border border-[#ead8c9] flex items-center gap-1.5">
            <Sparkles size={12} className="text-sterling-dark" />
            {service.badge}
          </div>
        )}
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-[#ead8c9] p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#daae9c]">
          Starting From
        </div>
        <div className="flex items-baseline gap-3 mt-1">
          <div className="font-serif-display text-5xl text-ink">
            {service.startingPrice}
          </div>
          <div className="text-sm text-ink/60">{service.priceNote}</div>
        </div>
        {service.priceFootnote && (
          <p className="mt-4 text-xs text-ink/55 leading-relaxed border-t border-[#ead8c9] pt-4">
            {service.priceFootnote}
          </p>
        )}
        <a href="#quote-form">
          <Button className="mt-5 w-full h-11 rounded-full bg-[#daae9c] hover:bg-white text-ink border border-transparent hover:border-[#daae9c] transition-colors">
            Request this package
          </Button>
        </a>
      </div>
    </div>

    {/* Details */}
    <div className="lg:col-span-7">
      <div className="text-xs uppercase tracking-[0.25em] text-[#daae9c]">
        {service.subtitle}
      </div>
      <h3 className="font-serif-display text-4xl lg:text-5xl text-ink mt-2 leading-[1.05]">
        {service.title}
      </h3>
      <div className="mt-3 flex items-center gap-2 text-[#8a5d48]">
        <Heart size={14} className="fill-[#daae9c] text-[#daae9c]" />
        <span className="italic font-serif-display text-lg">
          {service.tagline}
        </span>
      </div>
      <p className="mt-4 text-ink/70 leading-relaxed">{service.description}</p>

      {/* Perfect for */}
      {service.perfectFor && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#daae9c] mb-3">
            Perfect For
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {service.perfectFor.map((p) => (
              <div
                key={p}
                className="flex items-start gap-2.5 p-3 bg-white/60 border border-[#ead8c9] rounded-lg text-sm text-ink"
              >
                <Check
                  size={14}
                  className="text-sterling-dark mt-0.5 shrink-0"
                  strokeWidth={3}
                />
                <span>{p}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* What's included */}
      <div className="mt-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#daae9c] mb-3">
          What’s Included
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {service.included.map((item) => (
            <div
              key={item.title}
              className="p-4 bg-white rounded-xl border border-[#ead8c9] hover:border-sterling-dark transition-colors"
            >
              <div className="flex items-start gap-2.5">
                <div className="w-6 h-6 rounded-full bg-[#faf3eb] flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={12} className="text-sterling-dark" strokeWidth={3} />
                </div>
                <div>
                  <div className="font-serif-display text-base text-ink leading-tight">
                    {item.title}
                  </div>
                  <div className="text-xs text-ink/65 mt-1 leading-relaxed">
                    {item.desc}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Property sizes (Airbnb turnover etc.) */}
      {service.packages && (
        <div className="mt-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#daae9c] mb-3">
            Property Sizes
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {service.packages.map((p) => (
              <div
                key={p.name}
                className="p-4 bg-white border border-[#ead8c9] rounded-xl text-center"
              >
                <div className="font-serif-display text-lg text-ink">
                  {p.name}
                </div>
                <div className="text-xs text-ink/60 mt-1">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add-ons */}
      {service.addons && (
        <div className="mt-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#daae9c] mb-3">
            Popular Add-Ons
          </div>
          <div className="flex flex-wrap gap-2">
            {service.addons.map((a) => (
              <div
                key={a.name}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-[#ead8c9] rounded-full text-xs"
              >
                <Plus size={12} className="text-sterling-dark" />
                <span className="text-ink">{a.name}</span>
                <span className="text-[#8a5d48]">· {a.price}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-ink/55 italic">
            Custom add-ons available — we’ll tailor the clean to suit your needs.
          </p>
        </div>
      )}
    </div>
  </div>
);

export default Services;
